import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef, HostListener, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, forkJoin, of, Observable } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GeneralLanguage, ProjectProfileService, ProjectProfile, ProfileFieldsResponse, AdminService, CharitySearchResult, OrganizationProjectService, OrganisationDropdownItem, CharityDropdownItem, GeneralService, FileUploadResponse, DynamicTranslationService, ProjectService, ProjectCreateUpdatePayload, GsUserService, AuthService } from '@gs-2.0/core';
import { DropdownItem } from '@gs-2.0/shared';
import { ToastService } from '../../services/toast.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MediaPreviewDialogComponent } from '../media-preview-dialog/media-preview-dialog.component';
import { ImageCropperComponent } from '../image-cropper/image-cropper.component';
import { PROJECT_FORM_CONFIG, ProjectFormFieldConfig, ProjectFormGroupName, ProjectFormTab, getProjectFieldsByGroup, buildProjectFormGroupStructure, buildTeamFormGroupStructure, buildTeamMemberControls, buildPartnerOrgControls, buildGoalsFormGroupStructure, buildGalleryFormGroupStructure } from './project-form.config';

export interface ProjectFormData {
  language: GeneralLanguage;
  projectUuid?: string; // For edit mode
  organisationUuid?: string;
  organisationName?: string;
  entityStatus?: string;
  /**
   * The charity's own status (LIVE / DRAFT / PENDING / ARCHIVED / IDLE / DECLINED), passed in
   * when the caller already has it (e.g. Charity Edit's Add Project button) so the create-mode
   * Live-charity gate doesn't need to re-fetch it.
   */
  organisationStatus?: string;
  /**
   * When true, a charity admin (GS_ORGANIZATION_ADMIN) may pick the charity per
   * project just like a project admin — the Select Charity field stays enabled
   * instead of being locked to their own charity. Set by the settings projects
   * listing when it opens the add/edit form.
   */
  allowCharitySelection?: boolean;
}

export enum ProjectFormStep {
  DETAILS = 0,
  TEAM = 1,
  GOALS = 2,
  GALLERY = 3
}

@Component({
  selector: 'app-project-form',
  standalone: false,
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.scss']
})
export class ProjectFormComponent implements OnInit, OnDestroy {
  ProjectFormStep = ProjectFormStep;
  currentStep: ProjectFormStep = ProjectFormStep.DETAILS;

  get isEditMode(): boolean {
    return !!this.data?.projectUuid;
  }

  /**
   * The Select Charity field is disabled/readonly when either:
   * - the charity is locked to a true charity admin's own charity, or
   * - the form is in edit mode — the charity of an existing project can't be changed.
   */
  get isCharitySelectionDisabled(): boolean {
    return this.isCharityLocked || this.isEditMode;
  }

  entityStatus = 'DRAFT';

  get isDraftStatus(): boolean {
    return this.entityStatus === 'DRAFT';
  }

  /**
   * Whether the Save as Draft button should render. Beyond a project that's already a draft, it
   * also shows whenever the charity-Live gate is blocking Submit (GIV-5504) — every Case A/B
   * message promises "this project can only be saved as a draft," so a project blocked mid-edit
   * needs the same escape hatch even if its own status isn't DRAFT yet.
   * ⚠️ Unverified against the backend: this is the first path that can send `submitted: false`
   * (via onSaveAsDraft's buildPayload) for a project whose current status ISN'T already DRAFT —
   * confirm the update endpoint reverts status to DRAFT as intended rather than something else.
   */
  get showSaveAsDraftButton(): boolean {
    return this.isDraftStatus || this.isCharityBlocked;
  }

  /**
   * The selected/associated charity's own status (GIV-5504: a project cannot be submitted while
   * its charity isn't Live) — resolved via `resolveCharityStatus()`. Null while unknown/not yet
   * resolved.
   */
  charityStatus: string | null = null;

  /** Charities that have never gone Live vs. ones that were Live and later went inactive read as different messages (Figma 392-7843/7844). */
  get charityBlockedCase(): 'A' | 'B' | null {
    if (!this.charityStatus || this.charityStatus === 'LIVE') return null;
    // Backend's EntityStatus enum (be-resource-server Organisation.status) also has EXPIRED,
    // beyond the 6 values this app's own CharityStatus enum names — read the same way as
    // ARCHIVED/IDLE: the charity was active and no longer is, not "never been Live".
    const wasLiveCase = ['ARCHIVED', 'IDLE', 'EXPIRED'];
    return wasLiveCase.includes(this.charityStatus) ? 'B' : 'A';
  }

  get isCharityBlocked(): boolean {
    return this.charityBlockedCase !== null;
  }

  detailsForm!: FormGroup;
  teamForm!: FormGroup;
  goalsForm!: FormGroup;
  galleryForm!: FormGroup;
  galleryFiles: Array<{ fileName: string; fileUrl: string; fileUuid: string; fileSize: string; fileType: string; hasError: boolean; errorMessage: string }> = [];
  addMemberForm!: FormGroup;
  addMemberPhotoUrl = '';
  addMemberPhotoUuid = '';
  addMemberTeamMemberUuid = '';
  teamMemberPhotoUrls: string[] = [];

  addPartnerOrgForm!: FormGroup;
  addMemberFormTouched = false;
  addPartnerOrgFormTouched = false;
  partnerOrgNameSelected = false;
  partnerOrgDisplayValue = '';
  partnerOrgSearchResults: CharitySearchResult[] = [];
  private partnerOrgSearchDebounce$ = new Subject<string>();
  activePartnerOrgMenuIndex: number | null = null;
  editingPartnerOrgIndex: number | null = null;

  get teamMembers(): FormArray {
    return this.teamForm.get('teamMembers') as FormArray;
  }

  get partnerOrganizations(): FormArray {
    return this.teamForm.get('partnerOrganizations') as FormArray;
  }

  isLoadingDropdowns = false;
  isSaving = false;
  isSubmitting = false;
  isCheckingName = false;
  isCharityAdmin = false;
  /**
   * True only for a true charity admin (GS_ORGANIZATION_ADMIN), who is scoped to a
   * single charity: the charity field is locked (disabled/readonly) and auto-filled.
   * Project admins (GS_PROJECT_MANAGER) are NOT locked — they pick a charity per project.
   */
  isCharityLocked = false;
  /**
   * The Giving Link is an internally-managed field: only a Foundation Admin or a
   * Super Admin may edit it. Everyone else gets it disabled + readonly in the
   * template, and buildGivingLinkPayloadValue() re-asserts the server value on
   * submit so a devtools-enabled input can't smuggle an edit through.
   */
  canEditGivingLink = false;
  showGalleryImageError = false;
  projectProfiles: ProjectProfile[] = [];
  fieldVisibility: Map<string, 'optional' | 'mandatory' | 'hidden'> = new Map();

  // Dropdown options
  causes: OrganisationDropdownItem[] = [];
  categories: OrganisationDropdownItem[] = [];
  countries: OrganisationDropdownItem[] = [];
  currencies: OrganisationDropdownItem[] = [];
  projectManagers: OrganisationDropdownItem[] = [];

  onNumericKeyDown(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  // Cause custom dropdown
  isCauseDropdownOpen = false;

  get selectedCauseName(): string {
    const uuid = this.detailsForm.get('projectDetails.cause')?.value;
    return this.causes.find(c => c.uuid === uuid)?.name ?? '';
  }

  get projectTimelineGroup(): FormGroup {
    return this.detailsForm.get('projectTimeline') as FormGroup;
  }

  @HostListener('document:click')
  closeCauseDropdown(): void {
    this.isCauseDropdownOpen = false;
    this.activePartnerOrgMenuIndex = null;
  }

  toggleCauseDropdown(): void {
    this.isCauseDropdownOpen = !this.isCauseDropdownOpen;
  }

  onCauseSelected(uuid: string): void {
    this.detailsForm.get('projectDetails.cause')?.setValue(uuid);
    this.detailsForm.get('projectDetails.cause')?.markAsTouched();
    this.isCauseDropdownOpen = false;
  }

  // Charity selector
  charityOptions: DropdownItem[] = [];
  selectedCharity: CharitySearchResult | null = null;
  /**
   * The raw organisation-dropdowns response (status included), cached from whichever of
   * `loadDropdowns()`/`refreshDropdowns()` last fetched it. GIV-5504's Live-charity gate reads
   * status from here only — no separate per-charity endpoint call. Since this list and the
   * target charity uuid can each become known from either of two independent async sources
   * (the dropdowns fetch itself, or the charity-admin auto-fill in `ngOnInit`), every place that
   * learns one re-attempts resolution against whatever the other currently holds.
   */
  private charityDropdownOptions: CharityDropdownItem[] = [];
  /** Giving Link as last loaded from the API — the value non-editors always send back. */
  private serverGivingLink = '';

  private destroy$ = new Subject<void>();
  private visitedSteps = new Set<ProjectFormStep>([ProjectFormStep.DETAILS]);
  completedSteps: Set<ProjectFormStep> = new Set();
  projectUuid = '';
  private isFormSaved = false;
  private projectUuidSubject = new Subject<string>();
  private isCreatingProjectViaUpload = false;
  uploadedVideo: { fileName: string; fileUrl: string; fileUuid: string } | null = null;
  uploadedCoverPhoto: { fileName: string; fileUrl: string; fileUuid: string } | null = null;
  isUploadingVideo = false;
  isUploadingCoverPhoto = false;
  videoUploadError = false;
  coverPhotoUploadError = false;

  @ViewChild('coverPhotoCropper') coverPhotoCropper!: ImageCropperComponent;
  @ViewChild('coverPhotoFileInput') coverPhotoFileInputRef?: ElementRef<HTMLInputElement>;
  private pendingCoverPhotoFileName = '';

  /** Only Details tab is enabled; Team/Goals/Gallery are disabled */
  steps: Array<{ step: ProjectFormStep; labelKey: string; number: number; disabled: boolean }> = [
    { step: ProjectFormStep.DETAILS, labelKey: 'settings.project_details_form.details_tab', number: 1, disabled: false },
    { step: ProjectFormStep.TEAM,    labelKey: 'settings.project_details_form.team_tab',    number: 2, disabled: false },
    { step: ProjectFormStep.GOALS,   labelKey: 'settings.project_details_form.goals_tab',   number: 3, disabled: true  },
    { step: ProjectFormStep.GALLERY, labelKey: 'settings.project_details_form.gallery_tab', number: 4, disabled: true  }
  ];

  readonly DESCRIPTION_MAX_LENGTH = 1000;

  constructor(
    public dialogRef: MatDialogRef<ProjectFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProjectFormData,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private projectProfileService: ProjectProfileService,
    private adminService: AdminService,
    private organizationProjectService: OrganizationProjectService,
    private generalService: GeneralService,
    private toastService: ToastService,
    private translationService: DynamicTranslationService,
    private projectService: ProjectService,
    private dialog: MatDialog,
    private gsUserService: GsUserService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.entityStatus = (this.data?.entityStatus || 'DRAFT').toUpperCase();
    this.initForms();
    this.applyGivingLinkPermission();
    this.loadDropdowns();
    this.setupPartnerOrgSearch();
    this.setupNameDuplicateCheck();

    if (!this.isEditMode && this.data?.organisationStatus) {
      this.charityStatus = this.data.organisationStatus.toUpperCase();
    }

    this.gsUserService.getUserInfo()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(response => {
        const role = response.response.role;
        const isCharityAdminRole = role === 'GS_ORGANIZATION_ADMIN';
        const isProjectManagerRole = role === 'GS_PROJECT_MANAGER';
        // Both charity admins and project admins keep the Project Profile section
        // hidden. (The givingLink field is handled by applyGivingLinkPermission(),
        // which locks it for every role except Foundation Admin / Super Admin.)
        if (isCharityAdminRole || isProjectManagerRole) {
          this.isCharityAdmin = true;
        }
        // Only a true charity admin is scoped to their own charity: lock the charity
        // field and auto-fill it. Project admins pick a charity per project (empty).
        // When opened with allowCharitySelection (from the settings projects listing),
        // a charity admin may also pick the charity, so keep the field enabled while
        // still pre-filling it with their own charity as an editable default.
        if (isCharityAdminRole) {
          this.isCharityLocked = !this.data?.allowCharitySelection;
          const entity = response.response.entityDto;
          if (entity?.uuid && entity?.name) {
            this.detailsForm.get('projectInfo')?.patchValue({ charityUuid: entity.uuid });
            this.selectedCharity = { uuid: entity.uuid, name: entity.name };
            if (!this.data?.organisationStatus) {
              this.resolveCharityStatus();
            }
          }
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * GIV-5504's Live-charity gate reads status only off the organisation-dropdowns response
   * (`ai-docs/context/backend-integration.md` — be-resource-server change), never a separate
   * per-charity endpoint call. The dropdowns list and the target charity uuid can each become
   * known first from either of two independent async sources — `loadDropdowns()`/
   * `refreshDropdowns()` (the list) or the charity-admin auto-fill in `ngOnInit` (the uuid) — so
   * every place that learns one calls this to re-attempt resolution against whatever the other
   * currently holds. A no-op until both are known; harmless to call speculatively.
   */
  private resolveCharityStatus(): void {
    const uuid = this.detailsForm?.get('projectInfo.charityUuid')?.value;
    if (!uuid) return;
    const match = this.charityDropdownOptions.find(o => o.uuid === uuid);
    if (!match?.status) return;
    this.charityStatus = match.status.toUpperCase();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.projectUuidSubject.complete();
  }

  private setupNameDuplicateCheck(): void {
    const nameCtrl = this.detailsForm.get('projectDetails.projectName');
    if (!nameCtrl) return;

    nameCtrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap((name: string) => {
        if (name?.trim()) {
          this.isCheckingName = true;
          this.cdr.markForCheck();
        }
      }),
      switchMap((name: string) => {
        if (!name?.trim()) {
          this.isCheckingName = false;
          this.clearDuplicateNameError(nameCtrl);
          return of(null);
        }
        const uuid = this.projectUuid || this.data?.projectUuid || null;
        return this.projectService.checkProjectName({ name: name.trim(), uuid });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isCheckingName = false;
        if (response?.response === true) {
          nameCtrl.setErrors({ ...(nameCtrl.errors || {}), duplicateName: true });
        } else {
          this.clearDuplicateNameError(nameCtrl);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isCheckingName = false;
        this.clearDuplicateNameError(nameCtrl);
        this.cdr.markForCheck();
      }
    });
  }

  private clearDuplicateNameError(ctrl: AbstractControl): void {
    if (!ctrl.errors) return;
    const { duplicateName, ...remaining } = ctrl.errors;
    ctrl.setErrors(Object.keys(remaining).length ? remaining : null);
  }

  private setupPartnerOrgSearch(): void {
    this.partnerOrgSearchDebounce$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        if (query && query.trim().length > 0) {
          this.adminService.searchCharities(query).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
              this.partnerOrgSearchResults = response.response || [];
              this.cdr.markForCheck();
            },
            error: () => {
              this.partnerOrgSearchResults = [];
            }
          });
        } else {
          this.partnerOrgSearchResults = [];
        }
      });
  }

  onPartnerOrgInputChange(value: string): void {
    this.partnerOrgDisplayValue = value;
    this.addPartnerOrgForm.patchValue({ name: value });
    this.partnerOrgNameSelected = false;
    const nameCtrl = this.addPartnerOrgForm.get('name');
    if (value) {
      nameCtrl?.setErrors({ required: true });
    } else {
      nameCtrl?.setErrors(null);
      nameCtrl?.updateValueAndValidity();
    }
    this.partnerOrgSearchDebounce$.next(value);
  }

  onPartnerOrgSelected(org: CharitySearchResult): void {
    this.partnerOrgDisplayValue = org.name;
    this.addPartnerOrgForm.patchValue({ name: org.name });
    this.partnerOrgNameSelected = true;
    this.addPartnerOrgForm.get('name')?.setErrors(null);
    this.partnerOrgSearchResults = [];
  }

  onCharityChange(uuid: string): void {
    const org = this.charityOptions.find(o => o.id === uuid);
    this.selectedCharity = org ? { uuid: org.id, name: org.label } : null;
    this.detailsForm.get('projectInfo')?.patchValue({ charityUuid: uuid });
    this.detailsForm.get('projectInfo.charityUuid')?.markAsTouched();
    this.charityStatus = null;
    if (uuid) {
      this.refreshDropdowns(uuid);
    }
  }

  private loadDropdowns(): void {
    this.isLoadingDropdowns = true;

    const projectData$ = this.isEditMode
      ? this.projectService.getProjectForEdit(this.data.projectUuid!, this.data.language?.code || 'eng')
      : of(null);

    projectData$.pipe(
      switchMap(projectData => {
        const rawData = projectData?.response || projectData;
        const organisationUuid = rawData?.organisationUuid || this.data.organisationUuid;

        const dropdowns$ = organisationUuid
          ? this.organizationProjectService.getOrganisationDropdownsWithContext(organisationUuid)
          : this.organizationProjectService.getOrganisationDropdowns();

        return forkJoin({
          dropdowns: dropdowns$,
          profiles: this.projectProfileService.getAllProfiles()
        }).pipe(map(result => ({ ...result, projectData: rawData })));
      })
    ).subscribe({
      next: ({ dropdowns, profiles, projectData }) => {
        this.causes = dropdowns.causes;
        this.categories = dropdowns.categories;
        this.countries = dropdowns.countries;
        this.currencies = dropdowns.currencies;
        this.setProjectManagers(dropdowns.projectManagers || []);
        this.charityOptions = (dropdowns.organisations || []).map(org => ({ id: org.uuid, label: org.name }));
        this.charityDropdownOptions = dropdowns.organisations || [];

        // The dropdowns endpoint doesn't reliably include the charity a project is
        // (or will be) locked to — without an option matching that uuid, app-select
        // can't resolve a label for it and shows blank despite the form holding the
        // correct value. Seed it in from whichever source actually names it.
        const knownOrgUuid = projectData?.organisationUuid || this.data.organisationUuid;
        const knownOrgName = projectData?.organisationName || this.data.organisationName;
        if (knownOrgUuid && knownOrgName && !this.charityOptions.some(o => o.id === knownOrgUuid)) {
          this.charityOptions = [...this.charityOptions, { id: knownOrgUuid, label: knownOrgName }];
        }

        this.projectProfiles = profiles.data;
        this.isLoadingDropdowns = false;

        if (projectData) {
          this.populateFormFromApiData(projectData);
        } else {
          this.selectDefaultProjectProfile();
          if (this.data.organisationUuid) {
            this.detailsForm.get('projectInfo')?.patchValue({ charityUuid: this.data.organisationUuid });
            if (this.data.organisationName) {
              this.selectedCharity = { uuid: this.data.organisationUuid, name: this.data.organisationName };
            }
          }
        }
        this.resolveCharityStatus();

        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load project form dropdowns:', error);
        this.isLoadingDropdowns = false;
      }
    });
  }

  private refreshDropdowns(organisationUuid: string): void {
    this.organizationProjectService.getOrganisationDropdownsWithContext(organisationUuid).subscribe({
      next: (dropdowns) => {
        this.causes = dropdowns.causes;
        this.categories = dropdowns.categories;
        this.countries = dropdowns.countries;
        this.currencies = dropdowns.currencies;
        this.setProjectManagers(dropdowns.projectManagers || []);
        this.charityDropdownOptions = dropdowns.organisations || [];
        this.resolveCharityStatus();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to refresh dropdowns for organisation:', error);
      }
    });
  }

  private setProjectManagers(managers: OrganisationDropdownItem[]): void {
    this.projectManagers = managers;
    const control = this.teamForm?.get('teamOverview.projectManager');
    if (!control) return;
    if (managers.length === 0) {
      control.disable();
    } else {
      control.enable();
    }
  }

  private populateFormFromApiData(rawData: any): void {
    if (!rawData) return;

    // Set project UUID for save/update calls
    if (rawData.projectUuid) {
      this.projectUuid = rawData.projectUuid;
      this.isFormSaved = true;
    }

    if (rawData.entityStatus) {
      this.entityStatus = rawData.entityStatus.toUpperCase();
    }

    // ── Project Info ────────────────────────────────────────────
    const profileUuid = rawData.mandatoryProfile?.uuid
      || rawData.mandatoryProfileUuid
      || null;
    this.detailsForm.get('projectInfo')?.patchValue({
      projectProfile: profileUuid,
      charityUuid: rawData.organisationUuid || ''
    });
    if (profileUuid) {
      this.loadProfileFieldConfiguration(profileUuid);
    }
    if (rawData.organisationUuid && rawData.organisationName) {
      this.selectedCharity = { uuid: rawData.organisationUuid, name: rawData.organisationName };
    }

    // ── Project Details ─────────────────────────────────────────
    // Cause: prefer raw UUID field, fall back to name-matching
    let causeUuid: string | null = null;
    if (rawData.cause) {
      causeUuid = typeof rawData.cause === 'string' ? rawData.cause : rawData.cause?.uuid || null;
    } else if (rawData.projectCauseName) {
      causeUuid = this.causes.find(c => c.name === rawData.projectCauseName)?.uuid ?? null;
    }

    // Categories: check response fields in priority order
    let categoryUuids: string[] = [];
    const categorySource = rawData.categories || rawData.projectCategories || rawData.projectCategoriesNames;
    if (categorySource?.length) {
      categoryUuids = categorySource
        .map((c: any) => (typeof c === 'string'
          ? (this.categories.find(cat => cat.name === c)?.uuid ?? c)
          : c?.uuid))
        .filter(Boolean);
    }

    // Countries: check response fields in priority order
    let countryUuids: string[] = [];
    const countrySource = rawData.countriesList || rawData.projectCountries || rawData.projectCountriesNames;
    if (countrySource?.length) {
      countryUuids = countrySource
        .map((c: any) => (typeof c === 'string'
          ? (this.countries.find(cnt => cnt.name === c)?.uuid ?? c)
          : c?.uuid))
        .filter(Boolean);
    }

    this.detailsForm.get('projectDetails')?.patchValue({
      projectName: rawData.projectName || '',
      cause: causeUuid,
      category: categoryUuids,
      country: countryUuids,
      website: rawData.website || ''
    });

    // ── Social Media ────────────────────────────────────────────
    this.detailsForm.get('socialMedia')?.patchValue({
      linkedin: rawData.socialMediaLinkedin || rawData.projectSocialMediaLinkedin || '',
      twitter: rawData.socialMediaTwitter || rawData.projectSocialMediaTwitter || '',
      facebook: rawData.socialMediaFacebook || rawData.projectSocialMediaFacebook || '',
      instagram: rawData.socialMediaInstagram || rawData.projectSocialMediaInstagram || ''
    });

    // ── Project Timeline ────────────────────────────────────────
    const toDateString = (ts: number | null | undefined): string => {
      if (!ts) return '';
      return new Date(ts).toISOString().split('T')[0];
    };

    // Currency: prefer UUID from currency ref objects
    let currencyUuid: string | null = null;
    const currencyRef = rawData.amountRaisedToDateCurrency || rawData.totalProjectBudgetCurrency;
    if (currencyRef) {
      currencyUuid = typeof currencyRef === 'string' ? currencyRef : currencyRef?.uuid || null;
    } else if (rawData.amountRaisedToDateCurrencySymbol) {
      currencyUuid = this.currencies.find(c => c.name === rawData.amountRaisedToDateCurrencySymbol)?.uuid ?? null;
    }

    this.detailsForm.get('projectTimeline')?.patchValue({
      targetPopulation: rawData.targetPopulation || rawData.projectTargetPopulation || '',
      startDate: toDateString(rawData.startDate),
      endDate: toDateString(rawData.endDate),
      currency: currencyUuid,
      amountRaised: rawData.amountRaisedToDate ?? null,
      totalBudget: rawData.totalProjectBudget ?? null,
      givingLink: rawData.givingLink || ''
    });

    // Snapshot the server-side Giving Link — buildGivingLinkPayloadValue() sends this
    // back unchanged for users who aren't allowed to edit it.
    this.serverGivingLink = rawData.givingLink || '';

    // ── Project Content ─────────────────────────────────────────
    this.detailsForm.get('projectContent')?.patchValue({
      description: rawData.description || '',
      problem: rawData.problem || '',
      solutions: rawData.solution || '',
      activities: rawData.activities || ''
    });

    // ── Video & Cover Photo ─────────────────────────────────────
    const extractFileName = (path: string): string => path?.split('/').pop() || path;

    const videoDto = rawData.projectVideo || rawData.projectVideoDto;
    if (videoDto?.uuid) {
      this.uploadedVideo = {
        fileName: extractFileName(videoDto.filename || videoDto.fileName || 'Project Video'),
        fileUrl: videoDto.url,
        fileUuid: videoDto.uuid
      };
    }
    // ── Cover Photo & Gallery Images ────────────────────────────
    // Split by cover flag (cover: true → Details tab, cover: false/null → Gallery tab)
    const allImages: any[] = rawData.projectImages || rawData.projectImagesDto || [];
    const coverImgEntry = allImages.find((img: any) => {
      const media = img.mediaDto || img;
      return media.cover === true;
    });
    const coverImg = coverImgEntry || rawData.projectCoverImageDto;
    if (coverImg?.mediaDto?.uuid || coverImg?.uuid) {
      const img = coverImg.mediaDto || coverImg;
      this.uploadedCoverPhoto = {
        fileName: extractFileName(img.filename || img.fileName || 'Cover Photo'),
        fileUrl: img.url,
        fileUuid: img.uuid
      };
    }

    // ── Team Overview ───────────────────────────────────────────
    this.teamForm.get('teamOverview')?.patchValue({
      teamSummary: rawData.teamSummary || '',
      projectManager: rawData.projectManager?.uuid || null
    });

    // ── Goals Overview ──────────────────────────────────────────
    this.goalsForm.get('goalsOverview')?.patchValue({
      resultsMeasurement: rawData.resultsMeasurement || ''
    });

    // ── Gallery Images ──────────────────────────────────────────
    // All non-cover images go to the gallery tab
    const galleryImgs = allImages.filter((img: any) => {
      const media = img.mediaDto || img;
      return !media.cover;
    });
    this.galleryFiles = galleryImgs.map((img: any) => {
      const mediaDto = img.mediaDto || img;
      const rawName = mediaDto.filename || mediaDto.fileName || 'Image';
      const fileSize = mediaDto.fileSize || mediaDto.size || 0;
      const ext = (rawName.split('.').pop() || '').toLowerCase();
      const fileType = ['mp4', 'mov', 'avi', 'webm', '3gp', 'mpeg'].includes(ext) ? 'video/mp4' : 'image/jpeg';
      return {
        fileName: rawName.split('/').pop() || rawName,
        fileUrl: mediaDto.url || '',
        fileUuid: mediaDto.uuid || '',
        fileSize: fileSize > 0 ? this.formatFileSize(fileSize) : '',
        fileType,
        hasError: false,
        errorMessage: ''
      };
    });

    // ── Team Members ────────────────────────────────────────────
    const teamMembersSource = rawData.teamMembersList || rawData.teamMembers;
    if (teamMembersSource?.length) {
      teamMembersSource.forEach((member: any) => {
        const memberGroup = this.createTeamMember();
        const memberFullName = member.fullName || member.name || '';
        const memberTitle = member.title || member.jobTitle || member.role || '';
        const photoUrl = member.profilePicture?.url || member.image?.url || '';
        memberGroup.patchValue({
          uuid: member.uuid || null,
          nameTitle: memberFullName && memberTitle ? `${memberFullName}, ${memberTitle}` : memberFullName || memberTitle,
          email: (member.email || '').trim(),
          bio: member.bio || '',
          photoUrl,
          photoUuid: member.profilePicture?.uuid || member.image?.id || ''
        });
        this.teamMemberPhotoUrls.push(photoUrl);
        this.teamMembers.push(memberGroup);
      });
    }

    // ── Partner Organizations ───────────────────────────────────
    const partnerOrgsSource = rawData.partnerOrganisationsList || rawData.projectPartnerOrganisation;
    if (partnerOrgsSource?.length) {
      partnerOrgsSource.forEach((partner: any) => {
        const orgGroup = this.createPartnerOrg();
        orgGroup.patchValue({
          name: partner.name || '',
          description: partner.relationshipDescription || partner.description || ''
        });
        this.partnerOrganizations.push(orgGroup);
      });
    }


    this.cdr.markForCheck();
  }

  /**
   * Only a Foundation Admin / Super Admin may edit the Giving Link. For every other
   * role the control is disabled here (which also renders `disabled` on the input) —
   * the template additionally binds `readonly`, and buildGivingLinkPayloadValue()
   * is the authoritative guard for the submitted value.
   *
   * Both foundation-admin spellings are in use across the codebase
   * (see settings/projects.component.ts), so accept either.
   */
  private applyGivingLinkPermission(): void {
    this.canEditGivingLink = ['GS_SUPERADMIN', 'GS_FOUNDATION_ADMIN', 'GS_ADMIN_FOUNDATION']
      .some(role => this.authService.hasRole(role));
    if (!this.canEditGivingLink) {
      this.detailsForm?.get('projectTimeline.givingLink')?.disable({ emitEvent: false });
    }
  }

  /**
   * Resolves the Giving Link to submit. Non-editors always send back the value the
   * API last returned, so re-enabling the input via browser devtools and typing a new
   * URL cannot change what is persisted (buildPayload() reads projectTimeline with
   * getRawValue(), which includes disabled controls, so the value can't be trusted).
   */
  private buildGivingLinkPayloadValue(formGivingLink: string | null | undefined): string | null {
    if (!this.canEditGivingLink) {
      return this.serverGivingLink || null;
    }
    return formGivingLink || null;
  }

  private initForms(): void {
    const detailsStructure = buildProjectFormGroupStructure();
    const detailsFormGroups: any = {};
    Object.keys(detailsStructure).forEach(groupName => {
      detailsFormGroups[groupName] = this.fb.group(detailsStructure[groupName]);
    });
    this.detailsForm = this.fb.group(detailsFormGroups);

    const teamStructure = buildTeamFormGroupStructure();
    const teamFormGroups: any = {};
    Object.keys(teamStructure).forEach(groupName => {
      teamFormGroups[groupName] = this.fb.group(teamStructure[groupName]);
    });
    this.teamForm = this.fb.group({
      ...teamFormGroups,
      teamMembers: this.fb.array([]),
      partnerOrganizations: this.fb.array([])
    });

    this.addMemberForm = this.fb.group(buildTeamMemberControls());
    this.addPartnerOrgForm = this.fb.group(buildPartnerOrgControls());

    this.addMemberForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const allEmpty = ['nameTitle', 'email', 'bio'].every(f => !this.hasMandatoryValue(this.addMemberForm.get(f)?.value));
      if (allEmpty) {
        this.addMemberForm.markAsUntouched();
        this.addMemberFormTouched = false;
        this.cdr.markForCheck();
      }
    });

    this.addPartnerOrgForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const allEmpty = ['name', 'description'].every(f => !this.hasMandatoryValue(this.addPartnerOrgForm.get(f)?.value));
      if (allEmpty) {
        this.addPartnerOrgForm.markAsUntouched();
        this.addPartnerOrgFormTouched = false;
        this.cdr.markForCheck();
      }
    });

    const goalsStructure = buildGoalsFormGroupStructure();
    const goalsFormGroups: any = {};
    Object.keys(goalsStructure).forEach(groupName => {
      goalsFormGroups[groupName] = this.fb.group(goalsStructure[groupName]);
    });
    this.goalsForm = this.fb.group(goalsFormGroups);

    const galleryStructure = buildGalleryFormGroupStructure();
    const galleryFormGroups: any = {};
    Object.keys(galleryStructure).forEach(groupName => {
      galleryFormGroups[groupName] = this.fb.group(galleryStructure[groupName]);
    });
    this.galleryForm = this.fb.group(galleryFormGroups);
  }

  // ─── Team Tab ─────────────────────────────────────────────────────

  private createTeamMember(): FormGroup {
    return this.fb.group(buildTeamMemberControls());
  }

  getTeamMemberForm(index: number): FormGroup {
    return this.teamMembers.at(index) as FormGroup;
  }

  onTeamMemberPhotoFile(index: number, file: File): void {
    this.uploadTeamMemberPhoto(index, file);
  }

  onAddMemberPhotoFile(file: File): void {
    this.uploadAddMemberPhoto(file);
  }

  addTeamMember(): void {
    this.teamMembers.push(this.createTeamMember());
  }

  removeTeamMember(index: number): void {
    this.teamMembers.removeAt(index);
    this.teamMemberPhotoUrls.splice(index, 1);
  }

  private createPartnerOrg(): FormGroup {
    return this.fb.group(buildPartnerOrgControls());
  }

  addPartnerOrg(): void {
    this.partnerOrganizations.push(this.createPartnerOrg());
  }

  removePartnerOrg(index: number): void {
    this.partnerOrganizations.removeAt(index);
    this.activePartnerOrgMenuIndex = null;
    if (this.editingPartnerOrgIndex === index) {
      this.cancelEditPartnerOrg();
    }
  }

  togglePartnerOrgMenu(event: MouseEvent, index: number): void {
    event.stopPropagation();
    this.activePartnerOrgMenuIndex = this.activePartnerOrgMenuIndex === index ? null : index;
  }

  editPartnerOrg(index: number): void {
    const org = this.partnerOrganizations.at(index);
    this.editingPartnerOrgIndex = index;
    this.addPartnerOrgForm.patchValue({
      name: org.get('name')?.value,
      description: org.get('description')?.value
    });
    this.partnerOrgDisplayValue = org.get('name')?.value || '';
    this.partnerOrgNameSelected = true;
    this.activePartnerOrgMenuIndex = null;
  }

  cancelEditPartnerOrg(): void {
    this.editingPartnerOrgIndex = null;
    this.addPartnerOrgForm.reset();
    this.addPartnerOrgFormTouched = false;
    this.partnerOrgNameSelected = false;
    this.partnerOrgDisplayValue = '';
    this.partnerOrgSearchResults = [];
  }

  commitNewPartnerOrg(): void {
    this.addPartnerOrgFormTouched = true;
    this.addPartnerOrgForm.markAllAsTouched();
    if (this.addPartnerOrgForm.invalid) {
      this.cdr.markForCheck();
      return;
    }

    if (this.editingPartnerOrgIndex !== null) {
      const org = this.partnerOrganizations.at(this.editingPartnerOrgIndex);
      org.patchValue(this.addPartnerOrgForm.value);
      this.editingPartnerOrgIndex = null;
    } else {
      const org = this.createPartnerOrg();
      org.patchValue(this.addPartnerOrgForm.value);
      this.partnerOrganizations.push(org);
    }
    this.addPartnerOrgForm.reset();
    this.addPartnerOrgFormTouched = false;
    this.partnerOrgNameSelected = false;
    this.partnerOrgDisplayValue = '';
    this.partnerOrgSearchResults = [];
  }

  clearProjectManager(): void {
    this.teamForm.get('teamOverview.projectManager')?.setValue(null);
  }

  getTeamCharCount(path: string): number {
    return (this.teamForm.get(path)?.value || '').length;
  }

  getGoalsCharCount(path: string): number {
    return (this.goalsForm.get(path)?.value || '').length;
  }

  commitNewTeamMember(): void {
    this.addMemberFormTouched = true;
    this.addMemberForm.markAllAsTouched();
    const requiredFields = ['nameTitle', 'email', 'bio'];
    const hasInvalid = requiredFields.some(name => {
      if (!this.isFieldRequired(name)) return false;
      const value = this.addMemberForm.get(name)?.value;
      return !value || (typeof value === 'string' && !value.trim());
    });
    if (hasInvalid) {
      this.cdr.markForCheck();
      return;
    }

    const values = this.addMemberForm.value;
    const member = this.createTeamMember();
    member.patchValue({
      ...values,
      uuid: this.addMemberTeamMemberUuid || null,
      photoUrl: this.addMemberPhotoUrl,
      photoUuid: this.addMemberPhotoUuid
    });
    this.teamMemberPhotoUrls.push(this.addMemberPhotoUrl);
    this.teamMembers.push(member);
    this.addMemberForm.reset();
    this.addMemberFormTouched = false;
    this.addMemberPhotoUrl = '';
    this.addMemberPhotoUuid = '';
    this.addMemberTeamMemberUuid = '';
  }

  onAddMemberPhotoClick(): void {
    const input = document.getElementById('addMemberPhotoInput') as HTMLInputElement;
    if (input) input.click();
  }

  onAddMemberPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.project_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }
    this.uploadAddMemberPhoto(file);
    input.value = '';
  }

  private uploadAddMemberPhoto(file: File): void {
    this.generalService.uploadFile({
      entityUuid: this.projectUuid,
      fileType: 'PROJECT_TEAM_MEMBER',
      secondUuid: this.addMemberTeamMemberUuid || undefined,
      file
    }).subscribe({
      next: (response: FileUploadResponse) => {
        if (response.response.objectUuid) {
          this.addMemberTeamMemberUuid = response.response.objectUuid;
          this.addMemberForm.patchValue({ uuid: response.response.objectUuid });
        }
        this.addMemberPhotoUrl = response.response.url;
        this.addMemberPhotoUuid = response.response.uuid;
        this.cdr.markForCheck();
      },
      error: () => {
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_cover_photo_upload_failed'));
      }
    });
  }

  onTeamMemberPhotoClick(index: number): void {
    const input = document.getElementById(`teamMemberPhotoInput-${index}`) as HTMLInputElement;
    if (input) input.click();
  }

  onTeamMemberPhotoSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.project_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }
    this.uploadTeamMemberPhoto(index, file);
    input.value = '';
  }

  private uploadTeamMemberPhoto(index: number, file: File): void {
    const member = this.teamMembers.at(index);
    const memberUuid = member?.get('uuid')?.value || undefined;
    this.generalService.uploadFile({
      entityUuid: this.projectUuid,
      fileType: 'PROJECT_TEAM_MEMBER',
      secondUuid: memberUuid,
      file
    }).subscribe({
      next: (response: FileUploadResponse) => {
        if (!memberUuid && response.response.objectUuid) {
          member?.patchValue({ uuid: response.response.objectUuid });
        }
        member?.patchValue({ photoUrl: response.response.url, photoUuid: response.response.uuid });
        this.teamMemberPhotoUrls[index] = response.response.url;
        this.cdr.markForCheck();
      },
      error: () => {
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_cover_photo_upload_failed'));
      }
    });
  }

  isStepActive(step: ProjectFormStep): boolean {
    return this.currentStep === step;
  }

  isStepCompleted(step: ProjectFormStep): boolean {
    return this.completedSteps.has(step);
  }

  /**
   * A tab is completed when:
   * - It has mandatory fields → all mandatory fields have a value
   * - All fields are optional → at least one field has any value
   *
   * Required/optional state comes from fieldVisibility (dynamic profile config).
   * Falls back to config validators when fieldVisibility is not yet loaded.
   */
  hasStepData(step: ProjectFormStep): boolean {
    if (this.fieldVisibility.size === 0) return false; // Wait for profile config to load

    const tab = this.stepToTabName(step);
    const tabFields = PROJECT_FORM_CONFIG.filter(f => f.tab === tab && this.isFieldVisibleForCompletion(f));
    const mandatoryFields = tabFields.filter(f => this.isFieldMandatoryForCompletion(f));

    if (mandatoryFields.length > 0) {
      return mandatoryFields.every(f => this.fieldHasValue(f));
    }
    return tabFields.some(f => this.fieldHasValue(f));
  }

  private stepToTabName(step: ProjectFormStep): ProjectFormTab {
    switch (step) {
      case ProjectFormStep.DETAILS: return 'details';
      case ProjectFormStep.TEAM:    return 'team';
      case ProjectFormStep.GOALS:   return 'goals';
      case ProjectFormStep.GALLERY: return 'gallery';
    }
  }

  /**
   * A field is visible for completion purposes unless explicitly hidden by the profile.
   * Before the profile loads (fieldVisibility empty), all fields are treated as visible.
   */
  private isFieldVisibleForCompletion(field: ProjectFormFieldConfig): boolean {
    if (!field.key) return true;
    if (this.fieldVisibility.size === 0) return true;
    return this.fieldVisibility.get(field.key) !== 'hidden';
  }

  /**
   * A field is mandatory when:
   * - fieldVisibility is loaded and marks it 'mandatory'
   * - fieldVisibility is not yet loaded → fall back to config's Validators.required
   */
  private isFieldMandatoryForCompletion(field: ProjectFormFieldConfig): boolean {
    if (!field.key) return false;
    return this.fieldVisibility.get(field.key) === 'mandatory';
  }

  /**
   * Returns true if a field currently has a non-empty value.
   * File fields and FormArray fields are handled separately.
   */
  private fieldHasValue(field: ProjectFormFieldConfig): boolean {
    if (field.key === 'projectVideo')        return this.uploadedVideo !== null;
    if (field.key === 'projectCoverImageDto') return this.uploadedCoverPhoto !== null;
    if (field.key === 'projectImages')        return this.galleryFiles.length > 0;
    if (field.group === 'teamMember') {
      const pendingHasName = this.hasMandatoryValue(this.addMemberForm.get('nameTitle')?.value);
      const allCommittedHaveField = this.teamMembers.controls.every(m =>
        this.hasMandatoryValue(m.get(field.name)?.value));
      const pendingComplete = !pendingHasName
        || this.hasMandatoryValue(this.addMemberForm.get(field.name)?.value);
      const hasAnyMember = this.teamMembers.length > 0 || pendingHasName;
      return hasAnyMember && allCommittedHaveField && pendingComplete;
    }
    if (field.group === 'partnerOrg') {
      const pendingNameSelected = this.partnerOrgNameSelected
        && this.hasMandatoryValue(this.addPartnerOrgForm.get('name')?.value);
      const allCommittedHaveField = this.partnerOrganizations.controls.every(org =>
        this.hasMandatoryValue(org.get(field.name)?.value));
      const pendingComplete = !pendingNameSelected
        || this.hasMandatoryValue(this.addPartnerOrgForm.get(field.name)?.value);
      const hasAnyOrg = this.partnerOrganizations.length > 0 || pendingNameSelected;
      return hasAnyOrg && allCommittedHaveField && pendingComplete;
    }
    const control = this.getControlForField(field.name);
    return this.hasMandatoryValue(control?.value);
  }

  isStepComplete(step: ProjectFormStep): boolean {
    if (this.isStepDisabled(step)) return false;
    if (!this.isEditMode && !this.visitedSteps.has(step)) return false;
    switch (step) {
      case ProjectFormStep.DETAILS:
        return this.canSaveAsDraft;
      case ProjectFormStep.TEAM:
        return this.teamForm?.valid ?? false;
      case ProjectFormStep.GOALS:
        return this.goalsForm?.valid ?? false;
      case ProjectFormStep.GALLERY:
        return this.galleryForm?.valid ?? false;
      default:
        return false;
    }
  }

  isStepDisabled(step: ProjectFormStep): boolean {
    if (step === ProjectFormStep.GALLERY) {
      return !this.projectUuid;
    }
    if (step === ProjectFormStep.GOALS) {
      return !this.projectUuid;
    }
    if (step === ProjectFormStep.TEAM && !this.isEditMode) {
      return !this.canSaveAsDraft;
    }
    return false;
  }

  goToStep(step: ProjectFormStep): void {
    if (step === this.currentStep) return;
    if (this.isStepDisabled(step)) {
      if (this.currentStep === ProjectFormStep.DETAILS && !this.canSaveAsDraft) {
        this.detailsForm.get('projectInfo.projectProfile')?.markAsTouched();
        this.detailsForm.get('projectInfo.charityUuid')?.markAsTouched();
        this.detailsForm.get('projectDetails.projectName')?.markAsTouched();
        this.cdr.markForCheck();
      }
      return;
    }

    // Validate required fields before moving forward
    if (step > this.currentStep && !this.validateCurrentStepBeforeLeaving()) {
      return;
    }

    // In add mode: auto-save as draft on first departure from DETAILS
    if (!this.isEditMode && this.currentStep === ProjectFormStep.DETAILS && !this.isFormSaved && this.canSaveAsDraft) {
      this.autoSaveAndNavigate(step);
      return;
    }

    if (this.hasStepData(this.currentStep)) {
      this.completedSteps.add(this.currentStep);
    } else {
      this.completedSteps.delete(this.currentStep);
    }
    this.visitedSteps.add(step);
    this.currentStep = step;
  }

  private autoSaveAndNavigate(step: ProjectFormStep): void {
    this.isSaving = true;
    this.cdr.markForCheck();
    this.projectService.createOrUpdateProject(this.buildPayload(false))
      .pipe(
        switchMap((response) => {
          if (response?.response?.uuid) {
            this.projectUuid = response.response.uuid;
          }
          this.isFormSaved = true;
          const orgUuid = this.detailsForm.get('projectInfo.charityUuid')?.value;
          if (!orgUuid) return of(null);
          return this.organizationProjectService.getOrganisationDropdownsWithContext(orgUuid).pipe(
            catchError(() => of(null))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (dropdowns) => {
          if (dropdowns) {
            this.causes = dropdowns.causes;
            this.categories = dropdowns.categories;
            this.countries = dropdowns.countries;
            this.currencies = dropdowns.currencies;
            this.setProjectManagers(dropdowns.projectManagers || []);
          }
          this.isSaving = false;
          this.toastService.success(this.translationService.instant('settings.project_details_form.charity_added_success'));
          if (this.hasStepData(ProjectFormStep.DETAILS)) {
            this.completedSteps.add(ProjectFormStep.DETAILS);
          }
          this.visitedSteps.add(step);
          this.currentStep = step;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSaving = false;
          this.handleSaveError(error);
          this.cdr.markForCheck();
        }
      });
  }

  private handleSaveError(error: any): void {
    if (error?.error?.message === 'PROJECT_MANAGER_INCOMPLETE_PROFILE') {
      this.toastService.error(this.translationService.instant('settings.project_details_form.project_manager_incomplete_profile'));
    } else {
      this.toastService.error(this.translationService.instant('settings.project_details_form.save_error'));
    }
  }

  get canSaveAsDraft(): boolean {
    const profile = this.detailsForm.get('projectInfo.projectProfile')?.value;
    const name = this.detailsForm.get('projectDetails.projectName')?.value;
    const charity = this.detailsForm.get('projectInfo.charityUuid')?.value;
    return !!profile && !!name?.trim() && !!charity;
  }

  get canSubmit(): boolean {
    if (!this.canSaveAsDraft) return false;
    if (this.isCharityBlocked) return false;
    if (this.isCheckingName) return false;
    if (this.fieldVisibility.size === 0) return false;

    for (const [key, visibility] of this.fieldVisibility) {
      if (visibility !== 'mandatory') continue;

      const fieldConfig = PROJECT_FORM_CONFIG.find(c => c.key === key);
      if (!fieldConfig) continue;

      // File fields
      if (key === 'projectVideo') { if (!this.uploadedVideo) return false; continue; }
      if (key === 'projectCoverImageDto') { if (!this.uploadedCoverPhoto) return false; continue; }
      if (key === 'projectImages') { if (!this.galleryFiles.length) return false; continue; }

      // Collection fields — mandatory means field is required when an entry exists,
      // not that the user must add entries. Skip entirely.
      if (fieldConfig.group === 'teamMember' || fieldConfig.group === 'partnerOrg') continue;

      // Regular form controls — use group-scoped lookup to avoid name collisions
      const control = this.getScopedControlForField(fieldConfig);
      if (!control) continue;
      if (!this.hasMandatoryValue(control.value)) return false;
    }

    return true;
  }

  private getScopedControlForField(fieldConfig: ProjectFormFieldConfig): AbstractControl | null {
    const detailsGroups = ['projectInfo', 'projectDetails', 'socialMedia', 'projectTimeline', 'projectContent'];
    if (detailsGroups.includes(fieldConfig.group)) {
      return this.detailsForm.get(fieldConfig.group)?.get(fieldConfig.name) ?? null;
    }
    if (fieldConfig.group === 'teamOverview') return this.teamForm.get('teamOverview')?.get(fieldConfig.name) ?? null;
    if (fieldConfig.group === 'goalsOverview') return this.goalsForm.get('goalsOverview')?.get(fieldConfig.name) ?? null;
    if (fieldConfig.group === 'galleryOverview') return this.galleryForm.get('galleryOverview')?.get(fieldConfig.name) ?? null;
    return null;
  }

  private getControlForField(fieldName: string): AbstractControl | null {
    for (const group of ['projectInfo', 'projectDetails', 'socialMedia', 'projectTimeline', 'projectContent']) {
      const ctrl = this.detailsForm.get(`${group}.${fieldName}`);
      if (ctrl) return ctrl;
    }
    return this.teamForm.get(`teamOverview.${fieldName}`)
        ?? this.goalsForm.get(`goalsOverview.${fieldName}`)
        ?? this.galleryForm.get(`galleryOverview.${fieldName}`)
        ?? null;
  }

  private hasMandatoryValue(value: any): boolean {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'string' && !value.trim()) return false;
    return true;
  }

  /**
   * Returns the entityUuid to use for a file upload.
   * When no projectUuid exists yet:
   *  - The first upload proceeds with '' (backend creates the project stub and returns objectUuid).
   *  - Any simultaneous upload waits for that objectUuid before calling the API, preventing
   *    two separate project stubs from being created.
   */
  private getEntityUuidForUpload(): Observable<string> {
    if (this.projectUuid) return of(this.projectUuid);
    if (!this.isCreatingProjectViaUpload) {
      this.isCreatingProjectViaUpload = true;
      return of('');
    }
    return this.projectUuidSubject.pipe(take(1));
  }

  private validateCurrentStepBeforeLeaving(): boolean {
    const tab = this.stepToTabName(this.currentStep);
    const mandatoryFields = PROJECT_FORM_CONFIG.filter(f =>
      f.tab === tab && this.isFieldMandatoryForCompletion(f)
    );

    let allValid = true;
    let memberFormMarked = false;
    let partnerFormMarked = false;

    for (const field of mandatoryFields) {
      if (field.key === 'projectVideo') {
        if (!this.uploadedVideo) { this.videoUploadError = true; allValid = false; }
        continue;
      }
      if (field.key === 'projectCoverImageDto') {
        if (!this.uploadedCoverPhoto) { this.coverPhotoUploadError = true; allValid = false; }
        continue;
      }
      if (field.group === 'teamMember') {
        if (!this.fieldHasValue(field)) {
          allValid = false;
          if (!memberFormMarked) {
            this.addMemberFormTouched = true;
            this.addMemberForm.markAllAsTouched();
            memberFormMarked = true;
          }
        }
        continue;
      }
      if (field.group === 'partnerOrg') {
        if (!this.fieldHasValue(field)) {
          allValid = false;
          if (!partnerFormMarked) {
            this.addPartnerOrgFormTouched = true;
            this.addPartnerOrgForm.markAllAsTouched();
            partnerFormMarked = true;
          }
        }
        continue;
      }

      const control = this.getControlForField(field.name);
      if (control) {
        control.markAsTouched();
        if (!this.hasMandatoryValue(control.value)) allValid = false;
      }
    }

    // Also block navigation if the user has partially filled the add-member or
    // add-partner-org forms without committing them, regardless of profile config.
    if (this.currentStep === ProjectFormStep.TEAM) {
      const memberVisibleFields = ['nameTitle', 'email', 'bio'];
      const memberHasInput = memberVisibleFields.some(f => this.hasMandatoryValue(this.addMemberForm.get(f)?.value));
      if (memberHasInput && this.addMemberForm.invalid) {
        this.addMemberForm.markAllAsTouched();
        allValid = false;
      }

      const partnerVisibleFields = ['name', 'description'];
      const partnerHasInput = partnerVisibleFields.some(f => this.hasMandatoryValue(this.addPartnerOrgForm.get(f)?.value));
      if (partnerHasInput && this.addPartnerOrgForm.invalid) {
        this.addPartnerOrgForm.markAllAsTouched();
        allValid = false;
      }
    }

    this.cdr.markForCheck();
    return allValid;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ─── Profile Field Visibility ──────────────────────────────────────────────

  onProjectProfileChange(): void {
    const selectedUuid = this.detailsForm.get('projectInfo.projectProfile')?.value;
    if (selectedUuid) {
      this.resetInlineErrors();
      this.loadProfileFieldConfiguration(selectedUuid);
    }
  }

  private resetInlineErrors(): void {
    this.videoUploadError = false;
    this.coverPhotoUploadError = false;
    this.addMemberFormTouched = false;
    this.addPartnerOrgFormTouched = false;
    this.detailsForm.markAsUntouched();
    this.teamForm.markAsUntouched();
    this.goalsForm.markAsUntouched();
    this.galleryForm.markAsUntouched();
    this.addMemberForm.markAsUntouched();
    this.addPartnerOrgForm.markAsUntouched();
    this.cdr.detectChanges();
  }

  private selectDefaultProjectProfile(): void {
    if (!this.projectProfiles?.length) return;
    const defaultProfile = this.projectProfiles.find(p => p.default === true);
    if (defaultProfile) {
      this.detailsForm.get('projectInfo')?.patchValue({ projectProfile: defaultProfile.uuid });
      this.loadProfileFieldConfiguration(defaultProfile.uuid);
    }
  }

  private loadProfileFieldConfiguration(profileUuid: string): void {
    this.projectProfileService.getProjectProfileFieldsById(profileUuid).subscribe({
      next: (response: ProfileFieldsResponse) => {
        this.applyProfileFieldConfiguration(response);
        this.resetInlineErrors();
      },
      error: (error) => {
        console.error('Error loading profile configuration:', error);
      }
    });
  }

  private applyProfileFieldConfiguration(response: ProfileFieldsResponse): void {
    if (!response.data?.innerFields) return;

    this.fieldVisibility.clear();

    this.flattenProfileFields(response.data.innerFields).forEach((apiField: any) => {
      let visibility: 'optional' | 'mandatory' | 'hidden' = 'optional';
      if (apiField.hidden === true) {
        visibility = 'hidden';
      } else if (apiField.hidden === false) {
        visibility = apiField.mandatory === false ? 'optional' : 'mandatory';
      }
      this.fieldVisibility.set(apiField.name, visibility);
    });

    this.applyVisibilityToForms();
    this.refreshVisibleSteps();

    // Re-evaluate step completion now that fieldVisibility is fully loaded (edit mode only)
    if (this.isEditMode) {
      [ProjectFormStep.DETAILS, ProjectFormStep.TEAM, ProjectFormStep.GOALS, ProjectFormStep.GALLERY].forEach(step => {
        if (this.hasStepData(step)) {
          this.completedSteps.add(step);
        } else {
          this.completedSteps.delete(step);
        }
      });
    }

    this.cdr.markForCheck();
  }

  private flattenProfileFields(fields: { [key: string]: any }): any[] {
    const result: any[] = [];
    for (const field of Object.values(fields)) {
      result.push(field);
      if (field.innerFields) {
        result.push(...this.flattenProfileFields(field.innerFields));
      }
    }
    return result;
  }

  private applyVisibilityToForms(): void {
    const detailsGroupNames = ['projectInfo', 'projectDetails', 'socialMedia', 'projectTimeline', 'projectContent'];

    PROJECT_FORM_CONFIG.forEach(fieldConfig => {
      if (!fieldConfig.key) return;

      const visibility = this.fieldVisibility.get(fieldConfig.key);
      let control: AbstractControl | null = null;

      if (detailsGroupNames.includes(fieldConfig.group)) {
        control = this.detailsForm.get(fieldConfig.group)?.get(fieldConfig.name) ?? null;
      } else if (fieldConfig.group === 'teamOverview') {
        control = this.teamForm.get('teamOverview')?.get(fieldConfig.name) ?? null;
      } else if (fieldConfig.group === 'goalsOverview') {
        control = this.goalsForm.get('goalsOverview')?.get(fieldConfig.name) ?? null;
      } else if (fieldConfig.group === 'galleryOverview') {
        control = this.galleryForm.get('galleryOverview')?.get(fieldConfig.name) ?? null;
      } else if (fieldConfig.group === 'teamMember') {
        control = this.addMemberForm.get(fieldConfig.name) ?? null;
      } else if (fieldConfig.group === 'partnerOrg') {
        control = this.addPartnerOrgForm.get(fieldConfig.name) ?? null;
      }

      if (!control) return;

      const configValidators = fieldConfig.validators || [];

      if (visibility === 'mandatory') {
        const validators = configValidators.includes(Validators.required)
          ? configValidators
          : [Validators.required, ...configValidators];
        control.setValidators(validators);
      } else if (visibility !== undefined) {
        // 'optional' or 'hidden' — remove required, keep other config validators
        const nonRequired = configValidators.filter(v => v !== Validators.required);
        control.setValidators(nonRequired.length > 0 ? nonRequired : null);
      }
      // visibility === undefined: field not in profile config, keep config validators as-is

      control.updateValueAndValidity();
    });
  }

  isFieldVisible(fieldName: string): boolean {
    const fieldConfig = PROJECT_FORM_CONFIG.find(c => c.name === fieldName);
    if (!fieldConfig?.key) return true;
    const visibility = this.fieldVisibility.get(fieldConfig.key);
    if (!visibility) return true;
    return visibility !== 'hidden';
  }

  isSectionVisible(groupName: ProjectFormGroupName): boolean {
    return getProjectFieldsByGroup(groupName).some(field => this.isFieldVisible(field.name));
  }

  isTabVisible(tab: ProjectFormTab): boolean {
    const fields = PROJECT_FORM_CONFIG.filter(f => f.tab === tab && f.labelKey);
    if (fields.length === 0) return true;
    return fields.some(field => {
      if (!field.key) return true;
      const visibility = this.fieldVisibility.get(field.key);
      if (!visibility) return true;
      return visibility !== 'hidden';
    });
  }

  private refreshVisibleSteps(): void {
    const allStepDefs: Array<{ step: ProjectFormStep; tab: ProjectFormTab; labelKey: string }> = [
      { step: ProjectFormStep.DETAILS, tab: 'details', labelKey: 'settings.project_details_form.details_tab' },
      { step: ProjectFormStep.TEAM,    tab: 'team',    labelKey: 'settings.project_details_form.team_tab' },
      { step: ProjectFormStep.GOALS,   tab: 'goals',   labelKey: 'settings.project_details_form.goals_tab' },
      { step: ProjectFormStep.GALLERY, tab: 'gallery', labelKey: 'settings.project_details_form.gallery_tab' },
    ];

    this.steps = allStepDefs
      .filter(s => this.isTabVisible(s.tab))
      .map((s, index) => ({ step: s.step, labelKey: s.labelKey, number: index + 1, disabled: false }));

    if (!this.steps.find(s => s.step === this.currentStep)) {
      this.currentStep = this.steps[0]?.step ?? ProjectFormStep.DETAILS;
    }
  }

  private getNextVisibleStep(): ProjectFormStep | null {
    const visibleSteps = this.steps.map(s => s.step).sort((a, b) => a - b);
    const idx = visibleSteps.indexOf(this.currentStep);
    return idx >= 0 && idx < visibleSteps.length - 1 ? visibleSteps[idx + 1] : null;
  }

  private getPreviousVisibleStep(): ProjectFormStep | null {
    const visibleSteps = this.steps.map(s => s.step).sort((a, b) => a - b);
    const idx = visibleSteps.indexOf(this.currentStep);
    return idx > 0 ? visibleSteps[idx - 1] : null;
  }

  isBackDisabled(): boolean {
    return this.getPreviousVisibleStep() === null;
  }

  isNextDisabled(): boolean {
    const next = this.getNextVisibleStep();
    if (next === null) return true;
    return this.isStepDisabled(next) || this.isSaving;
  }

  get currentStepLabelKey(): string {
    return this.steps.find(s => s.step === this.currentStep)?.labelKey ?? '';
  }

  isFieldVisibleByKey(key: string): boolean {
    const visibility = this.fieldVisibility.get(key);
    if (!visibility) return true;
    return visibility !== 'hidden';
  }

  isFieldRequired(fieldName: string): boolean {
    const fieldConfig = PROJECT_FORM_CONFIG.find(c => c.name === fieldName);
    if (!fieldConfig?.key) return fieldConfig?.validators?.includes(Validators.required) ?? false;
    const visibility = this.fieldVisibility.get(fieldConfig.key);
    return visibility === 'mandatory';
  }

  isFieldRequiredByKey(key: string): boolean {
    const visibility = this.fieldVisibility.get(key);
    return visibility === 'mandatory';
  }

  onSaveAsDraft(): void {
    if (!this.canSaveAsDraft) {
      this.detailsForm.get('projectInfo.projectProfile')?.markAsTouched();
      this.detailsForm.get('projectInfo.charityUuid')?.markAsTouched();
      this.detailsForm.get('projectDetails.projectName')?.markAsTouched();
      this.cdr.markForCheck();
      this.toastService.error(this.translationService.instant('settings.project_details_form.draft_min_fields_error'));
      return;
    }

    this.isSaving = true;
    this.projectService.createOrUpdateProject(this.buildPayload(false))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          if (response?.response?.uuid) {
            this.projectUuid = response.response.uuid;
          }
          this.isFormSaved = true;
          if (this.charityBlockedCase) {
            this.showCharityBlockedDraftPopup(this.charityBlockedCase);
          } else {
            this.toastService.success(this.translationService.instant('settings.project_details_form.draft_saved_success'));
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSaving = false;
          this.handleSaveError(error);
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Case A/B popup shown after a project is saved as a draft while its charity isn't Live
   * (GIV-5504) — the charity name and "Go to charity profile" button match the Figma copy.
   */
  private showCharityBlockedDraftPopup(blockedCase: 'A' | 'B'): void {
    const messageKey = blockedCase === 'A'
      ? 'settings.project_details_form.charity_not_live_draft_popup_case_a'
      : 'settings.project_details_form.charity_not_live_draft_popup_case_b';

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      maxWidth: '90vw',
      data: {
        message: messageKey,
        params: { charityName: this.selectedCharity?.name || '' },
        actionType: 'confirm',
        confirmButtonText: 'settings.project_details_form.go_to_charity_profile',
        cancelButtonText: 'common.close'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.goToCharityProfile();
      }
    });
  }

  private goToCharityProfile(): void {
    const charityUuid = this.detailsForm.get('projectInfo.charityUuid')?.value;
    if (!charityUuid) return;
    this.dialogRef.close({ action: 'save' });
    this.router.navigate(['/settings/charities/edit', charityUuid]);
  }

  onGalleryFilesChange(files: typeof this.galleryFiles): void {
    this.galleryFiles = files;
    if (files.length > 0) {
      this.showGalleryImageError = false;
    }
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      this.detailsForm.markAllAsTouched();
      this.teamForm.markAllAsTouched();
      this.goalsForm.markAllAsTouched();
      this.galleryForm.markAllAsTouched();
      if (this.isFieldRequiredByKey('projectImages') && this.galleryFiles.length === 0) {
        this.showGalleryImageError = true;
      }
      return;
    }

    this.detailsForm.markAllAsTouched();

    // Check allowProjectApproval setting to decide whether to show confirmation dialog
    this.generalService.getGeneralSettings().pipe(take(1)).subscribe({
      next: (response) => {
        const configMap = response.response.globalConfig.reduce((acc, item) => {
          acc[item.key] = item.value;
          return acc;
        }, {} as Record<string, string>);

        const allowProjectApproval = configMap['allowProjectApproval'] === 'true';

        if (allowProjectApproval) {
          // Show confirmation dialog before submitting
          const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '700px',
            maxWidth: '100vw',
            data: {
              message: 'settings.project_details_form.confirm_submission_message',
              actionType: 'archive',
              confirmButtonText: 'settings.project_details_form.submit',
              cancelButtonText: 'common.cancel'
            }
          });

          dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) return;
            this.executeSubmission();
          });
        } else {
          // Skip dialog and submit directly
          this.executeSubmission();
        }
      },
      error: () => {
        // On settings fetch failure, fall back to showing the confirmation dialog
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '700px',
          maxWidth: '100vw',
          data: {
            message: 'settings.project_details_form.confirm_submission_message',
            actionType: 'archive',
            confirmButtonText: 'settings.project_details_form.submit',
            cancelButtonText: 'common.cancel'
          }
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (!confirmed) return;
          this.executeSubmission();
        });
      }
    });
  }

  /**
   * Build and send the project submission payload to the API.
   * Called after confirmation (dialog or direct) depending on allowProjectApproval setting.
   */
  private executeSubmission(): void {
    this.isSubmitting = true;
    this.projectService.createOrUpdateProject(this.buildPayload(true))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response?.response?.uuid) {
            this.projectUuid = response.response.uuid;
          }
          this.toastService.success(this.translationService.instant('settings.project_details_form.submit_success'));
          this.dialogRef.close({ action: 'submit', response });
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.handleSaveError(error);
          this.cdr.markForCheck();
        }
      });
  }

  private mapTeamMemberToPayload(member: AbstractControl, index: number, photoUrl?: string, photoUuid?: string): any {
    const nameTitle = member.get('nameTitle')?.value || '';
    const separatorIdx = nameTitle.indexOf(', ');
    const fullName = separatorIdx >= 0 ? nameTitle.substring(0, separatorIdx) : nameTitle;
    const title = separatorIdx >= 0 ? nameTitle.substring(separatorIdx + 2) : '';
    const uuid = photoUuid ?? member.get('photoUuid')?.value;
    const url = photoUrl ?? member.get('photoUrl')?.value;
    return {
      uuid: member.get('uuid')?.value || null,
      position: index + 1,
      fullName,
      profilePicture: uuid
        ? { fileType: 'PROJECT_TEAM_MEMBER', allowedFileType: 'IMG', uuid, url, cover: false, pendingUpload: false, isFormatValid: true, progress: 0 }
        : { fileType: 'PROJECT_TEAM_MEMBER', allowedFileType: 'IMG', cover: false, pendingUpload: false, isFormatValid: true, progress: 0 },
      bio: member.get('bio')?.value || '',
      linkedinProfile: null,
      email: member.get('email')?.value || '',
      title
    };
  }

  private buildTeamMembersPayload(): any[] {
    const members = this.teamMembers.controls.map((member, index) =>
      this.mapTeamMemberToPayload(member, index)
    );
    const pendingName = this.addMemberForm.get('nameTitle')?.value;
    if (pendingName) {
      members.push(this.mapTeamMemberToPayload(
        this.addMemberForm,
        members.length,
        this.addMemberPhotoUrl,
        this.addMemberPhotoUuid
      ));
    }
    return members;
  }

  private buildPayload(submitted: boolean): ProjectCreateUpdatePayload {
    const pi = this.detailsForm.get('projectInfo')?.value;
    const pd = this.detailsForm.get('projectDetails')?.value;
    const sm = this.detailsForm.get('socialMedia')?.value;
    const pt = (this.detailsForm.get('projectTimeline') as FormGroup)?.getRawValue();
    const pc = this.detailsForm.get('projectContent')?.value;
    const to = this.teamForm.get('teamOverview')?.value;
    const go = this.goalsForm.get('goalsOverview')?.value;

    const currencyUuid: string | null = pt?.currency ?? null;
    const currency = currencyUuid
      ? (this.currencies.find(c => c.uuid === currencyUuid) ?? null)
      : null;
    const currencyRef = currency ? { uuid: currency.uuid, name: currency.name } : null;

    const causeUuid: string | null = pd?.cause ?? null;
    const causeRef = causeUuid
      ? (this.causes.find(c => c.uuid === causeUuid) ?? null)
      : null;
    const causeObj = causeRef ? { uuid: causeRef.uuid, name: causeRef.name } : null;

    const categoryObjs = ((pd?.category ?? []) as string[])
      .map(uuid => this.categories.find(c => c.uuid === uuid))
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map(c => ({ uuid: c.uuid, name: c.name }));

    const countryObjs = ((pd?.country ?? []) as string[])
      .map(uuid => this.countries.find(c => c.uuid === uuid))
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map(c => ({ uuid: c.uuid, name: c.name }));

    const toTimestamp = (dateStr: string | null): number | null => {
      if (!dateStr) return null;
      const ts = new Date(dateStr).getTime();
      return isNaN(ts) ? null : ts;
    };

    return {
      projectUuid: this.projectUuid || '',
      organisationUuid: pi?.charityUuid ?? '',
      mandatoryProfileUuid: pi?.projectProfile ?? '',
      languageUuid: this.data.language?.uuid ?? '',
      projectName: pd?.projectName ?? '',
      cause: causeObj,
      projectCategories: categoryObjs,
      projectCountries: countryObjs,
      website: pd?.website || null,
      socialMediaLinkedin: sm?.linkedin || null,
      socialMediaTwitter: sm?.twitter || null,
      socialMediaFacebook: sm?.facebook || null,
      socialMediaInstagram: sm?.instagram || null,
      targetPopulation: pt?.targetPopulation || null,
      startDate: toTimestamp(pt?.startDate),
      endDate: toTimestamp(pt?.endDate),
      amountRaisedToDate: pt?.amountRaised ?? null,
      amountRaisedToDateCurrency: currencyRef,
      totalProjectBudget: pt?.totalBudget ?? null,
      totalProjectBudgetCurrency: currencyRef,
      givingLink: this.buildGivingLinkPayloadValue(pt?.givingLink),
      description: pc?.description || null,
      problem: pc?.problem || null,
      solution: pc?.solutions || null,
      activities: pc?.activities || null,
      projectVideo: this.uploadedVideo
        ? { fileType: 'PROJECT_VIDEO', allowedFileType: 'VIDEO', uuid: this.uploadedVideo.fileUuid, fileName: this.uploadedVideo.fileName, url: this.uploadedVideo.fileUrl, cover: false, pendingUpload: false, isFormatValid: true, progress: 0 }
        : null,
      projectImages: this.buildProjectImagesPayload(),
      resultsMeasurement: go?.resultsMeasurement || null,
      teamSummary: to?.teamSummary || null,
      projectManagersUUID: to?.projectManager || null,
      teamMembers: this.buildTeamMembersPayload(),
      partnerOrganisations: (() => {
        const committed = this.partnerOrganizations.controls.map((org, index) => ({
          uuid: null,
          position: index + 1,
          name: org.get('name')?.value || '',
          relationshipDescription: org.get('description')?.value || ''
        }));
        const pendingNameValue = this.addPartnerOrgForm.get('name')?.value;
        const pendingValid = this.partnerOrgNameSelected && this.hasMandatoryValue(pendingNameValue);
        if (pendingValid) {
          committed.push({
            uuid: null,
            position: committed.length + 1,
            name: this.addPartnerOrgForm.get('name')?.value || '',
            relationshipDescription: this.addPartnerOrgForm.get('description')?.value || ''
          });
        }
        return committed;
      })(),
      projectDocuments: [],
      submitted,
    };
  }

  onBack(): void {
    const prevStep = this.getPreviousVisibleStep();
    if (prevStep !== null) {
      this.goToStep(prevStep);
    }
  }

  onNext(): void {
    const nextStep = this.getNextVisibleStep();
    if (nextStep !== null) {
      this.goToStep(nextStep);
    }
  }

  getCharCount(controlPath: string): number {
    const value = this.detailsForm.get(controlPath)?.value || '';
    return value.length;
  }

  // ─── Video Upload ────────────────────────────────────────────────

  onUploadVideoClick(): void {
    const input = document.getElementById('videoFileInput') as HTMLInputElement;
    if (input) input.click();
  }

  onVideoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/avi', 'video/mpeg', 'video/ogg', 'video/webm', 'video/3gpp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.project_details_form.error_invalid_video_type'));
      input.value = '';
      return;
    }
    this.uploadVideo(file);
    input.value = '';
  }

  private uploadVideo(file: File): void {
    this.isUploadingVideo = true;
    this.getEntityUuidForUpload().pipe(
      switchMap(uuid => this.generalService.uploadFile({ entityUuid: uuid, fileType: 'PROJECT_VIDEO', secondUuid: '', file }))
    ).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingVideo = false;
        if (!this.projectUuid && response.response?.objectUuid) {
          this.projectUuid = response.response.objectUuid;
        }
        if (this.isCreatingProjectViaUpload) {
          this.isCreatingProjectViaUpload = false;
          this.projectUuidSubject.next(this.projectUuid);
        }
        const rawVideoName = response.response.fileName || file.name;
        this.uploadedVideo = { fileName: rawVideoName.split('/').pop() || rawVideoName, fileUrl: response.response.url, fileUuid: response.response.uuid };
        this.videoUploadError = false;
        this.toastService.success(this.translationService.instant('settings.project_details_form.success_video_uploaded'));
      },
      error: () => {
        this.isUploadingVideo = false;
        if (this.isCreatingProjectViaUpload) {
          this.isCreatingProjectViaUpload = false;
          this.projectUuidSubject.next('');
        }
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_video_upload_failed'));
      }
    });
  }

  previewVideo(): void {
    if (!this.uploadedVideo?.fileUrl) return;
    this.dialog.open(MediaPreviewDialogComponent, {
      data: {
        url: this.uploadedVideo.fileUrl,
        fileName: this.uploadedVideo.fileName,
        fileType: this.getVideoFileType(this.uploadedVideo.fileName)
      },
      panelClass: 'media-preview-dialog',
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100vw',
      height: '100vh',
    });
  }

  private getVideoFileType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const map: { [key: string]: string } = {
      mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo',
      webm: 'video/webm', '3gp': 'video/3gpp', mpeg: 'video/mpeg'
    };
    return map[ext] || 'video/mp4';
  }

  deleteVideo(): void {
    if (!this.uploadedVideo || !this.projectUuid) return;
    this.generalService.deleteFile({ entityUuid: this.projectUuid, fileType: 'PROJECT_VIDEO', fileUuid: this.uploadedVideo.fileUuid }).subscribe({
      next: () => {
        this.uploadedVideo = null;
        this.toastService.success(this.translationService.instant('settings.project_details_form.success_video_deleted'));
      },
      error: () => {
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_video_delete_failed'));
      }
    });
  }

  // ─── Cover Photo Upload ──────────────────────────────────────────

  onUploadCoverPhotoClick(): void {
    this.coverPhotoFileInputRef?.nativeElement.click();
  }

  onCoverPhotoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.project_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }

    this.pendingCoverPhotoFileName = file.name;
    input.value = '';

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      this.coverPhotoCropper.open(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  onCoverPhotoCroppedBlob(blob: Blob): void {
    const file = new File([blob], this.pendingCoverPhotoFileName || 'cover-photo.jpg', { type: blob.type });
    this.uploadCoverPhoto(file);
  }

  private uploadCoverPhoto(file: File): void {
    this.isUploadingCoverPhoto = true;
    this.getEntityUuidForUpload().pipe(
      switchMap(uuid => this.generalService.uploadFile({ entityUuid: uuid, fileType: 'PROJECT_IMAGE', secondUuid: '', file }))
    ).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingCoverPhoto = false;
        if (!this.projectUuid && response.response?.objectUuid) {
          this.projectUuid = response.response.objectUuid;
        }
        if (this.isCreatingProjectViaUpload) {
          this.isCreatingProjectViaUpload = false;
          this.projectUuidSubject.next(this.projectUuid);
        }
        const rawPhotoName = response.response.fileName || file.name;
        this.uploadedCoverPhoto = { fileName: rawPhotoName.split('/').pop() || rawPhotoName, fileUrl: response.response.url, fileUuid: response.response.uuid };
        this.coverPhotoUploadError = false;
        this.toastService.success(this.translationService.instant('settings.project_details_form.success_cover_photo_uploaded'));
      },
      error: () => {
        this.isUploadingCoverPhoto = false;
        if (this.isCreatingProjectViaUpload) {
          this.isCreatingProjectViaUpload = false;
          this.projectUuidSubject.next('');
        }
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_cover_photo_upload_failed'));
      }
    });
  }

  previewCoverPhoto(): void {
    if (!this.uploadedCoverPhoto?.fileUrl) return;
    this.dialog.open(MediaPreviewDialogComponent, {
      data: {
        url: this.uploadedCoverPhoto.fileUrl,
        fileName: this.uploadedCoverPhoto.fileName,
        fileType: this.getImageFileType(this.uploadedCoverPhoto.fileName)
      },
      panelClass: 'media-preview-dialog',
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100vw',
      height: '100vh',
    });
  }

  private getImageFileType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const map: { [key: string]: string } = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
      bmp: 'image/bmp', webp: 'image/webp', jfif: 'image/jpeg', gif: 'image/gif'
    };
    return map[ext] || 'image/jpeg';
  }

  deleteCoverPhoto(): void {
    if (!this.uploadedCoverPhoto || !this.projectUuid) return;
    this.generalService.deleteFile({ entityUuid: this.projectUuid, fileType: 'PROJECT_IMAGE', fileUuid: this.uploadedCoverPhoto.fileUuid }).subscribe({
      next: () => {
        this.uploadedCoverPhoto = null;
        this.toastService.success(this.translationService.instant('settings.project_details_form.success_cover_photo_deleted'));
      },
      error: () => {
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_cover_photo_delete_failed'));
      }
    });
  }

  private buildProjectImagesPayload(): Array<{ mediaDto: any; position: number }> {
    const images: Array<{ mediaDto: any; position: number }> = [];
    let position = 1;

    if (this.uploadedCoverPhoto?.fileUuid) {
      images.push({
        mediaDto: {
          fileType: 'PROJECT_IMAGE',
          allowedFileType: 'IMG',
          uuid: this.uploadedCoverPhoto.fileUuid,
          fileName: this.uploadedCoverPhoto.fileName,
          url: this.uploadedCoverPhoto.fileUrl,
          cover: true,
          pendingUpload: false,
          isFormatValid: true,
          progress: 0,
          position
        },
        position
      });
      position++;
    }

    this.galleryFiles
      .filter(f => f.fileUuid && !f.hasError)
      .forEach(f => {
        images.push({
          mediaDto: {
            fileType: 'PROJECT_IMAGE',
            allowedFileType: 'IMG',
            uuid: f.fileUuid,
            fileName: f.fileName,
            url: f.fileUrl,
            cover: false,
            pendingUpload: false,
            isFormatValid: true,
            progress: 0,
            position
          },
          position
        });
        position++;
      });

    return images;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
